import { LoadingOutlined } from "@ant-design/icons"
import { Flex, Select, type SelectProps, Typography } from "antd"
import type { BaseOptionType } from "antd/lib/select"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useDebouncedCallback } from "../../../../../../../hooks/use-debounced-callback.ts"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import { getSupplierMaterials } from "../../../../../../SuppliersPage/api/services.ts"
import type { SupplierMaterial } from "../../../../../../SuppliersPage/types/types.ts"
import { normalize } from "./buildStakeholderOptions.ts"
import type {
  CatalogueMaterialPick,
  MaterialUnitBasis
} from "./material-line-derivation.ts"

const PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 500

// One control offers both a price-list row and the words the user typed, and a
// material id shares no id space with a name, so each option says which it is.
const CATALOGUE_PREFIX = "catalogue:"
const TYPED_PREFIX = "typed:"

type MaterialOption = BaseOptionType & {
  value: string
  label: string
  material?: SupplierMaterial
  /** The words the user typed, offered as its own row when no price list row matches. */
  typed?: boolean
}

interface MaterialSelectProps {
  /** The endpoint refuses an unscoped read, so with no supplier there is nothing to offer. */
  supplier: string | null
  basis: MaterialUnitBasis
  material: string | null
  materialName: string | null
  disabled?: boolean
  onPick: (material: CatalogueMaterialPick) => void
  onType: (name: string) => void
}

export function MaterialSelect({
  supplier,
  basis,
  material,
  materialName,
  disabled,
  onPick,
  onType
}: MaterialSelectProps) {
  const { t } = useTranslation()
  const [materials, setMaterials] = useState<SupplierMaterial[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  // Two searches can be in flight across the debounce boundary, and the slower
  // one must not repaint the newer term's rows.
  const latestQuery = useRef(0)

  const discardResults = () => {
    latestQuery.current += 1
    setMaterials([])
    setLoading(false)
  }

  const load = async (term: string) => {
    if (!supplier) {
      discardResults()
      return
    }

    const seq = ++latestQuery.current
    setLoading(true)

    try {
      const response = await getSupplierMaterials({
        supplier,
        q: term || null,
        page: 1,
        page_size: PAGE_SIZE
      })

      if (seq !== latestQuery.current) return

      setMaterials(response.ok ? (response.data?.results ?? []) : [])
    } finally {
      if (seq === latestQuery.current) setLoading(false)
    }
  }

  const onSearch = useDebouncedCallback((term: string) => {
    setSearch(term)
    void load(term.trim())
  }, SEARCH_DEBOUNCE_MS)

  // The rows are one supplier's price list, and `handleChange` resolves a pick
  // out of them — so carrying them across a supplier change would let the user
  // click the previous supplier's material and book its price against this one.
  // The gcc-scoped endpoint accepts that pairing, so there is no server backstop.
  useEffect(() => {
    discardResults()
  }, [supplier])

  const unitFigure = (row: SupplierMaterial) =>
    basis === "cost" ? row.unit_cost : row.unit_sale_price

  const loaded: MaterialOption[] = materials.map((row) => ({
    value: `${CATALOGUE_PREFIX}${row.id}`,
    label: row.name,
    material: row
  }))

  const selectedValue = material
    ? `${CATALOGUE_PREFIX}${material}`
    : materialName
      ? `${TYPED_PREFIX}${materialName}`
      : undefined

  // The line's own material need not be in the answer to the current term, and
  // a free-typed one is in no price list at all.
  const selectedOption: MaterialOption[] =
    selectedValue && !loaded.some((option) => option.value === selectedValue)
      ? [{ value: selectedValue, label: materialName ?? "" }]
      : []

  const typed = search.trim()
  const offerTyped =
    !!typed &&
    !loaded.some((option) => normalize(option.label) === normalize(typed)) &&
    `${TYPED_PREFIX}${typed}` !== selectedValue

  const options: MaterialOption[] = [
    ...selectedOption,
    ...loaded,
    ...(offerTyped
      ? [{ value: `${TYPED_PREFIX}${typed}`, label: typed, typed: true }]
      : [])
  ]

  const optionRender: SelectProps["optionRender"] = (option) => {
    const data = option.data as MaterialOption
    const row = data.material
    if (!row) {
      return (
        <Typography.Text>
          {data.typed
            ? t("finance.material.typedOption", { name: data.label })
            : data.label}
        </Typography.Text>
      )
    }

    const figure = unitFigure(row)
    const secondary = [
      row.unit,
      figure ? budgetFormat(Number(figure)) : null
    ]
      .filter(Boolean)
      .join(" · ")

    return (
      <Flex vertical className="leading-tight">
        <Typography.Text>{row.name}</Typography.Text>
        {!!secondary && (
          <Typography.Text type="secondary" className="text-xs">
            {secondary}
          </Typography.Text>
        )}
      </Flex>
    )
  }

  const handleChange = (value?: string) => {
    if (!value) {
      onType("")
      return
    }

    if (value.startsWith(TYPED_PREFIX)) {
      onType(value.slice(TYPED_PREFIX.length))
      return
    }

    const picked = materials.find(
      (row) => `${CATALOGUE_PREFIX}${row.id}` === value
    )
    if (!picked) return

    onPick({
      id: picked.id,
      name: picked.name,
      unit_sale_price: picked.unit_sale_price,
      unit_cost: picked.unit_cost
    })
  }

  return (
    <Select<string>
      showSearch
      allowClear
      className="w-full"
      disabled={disabled || !supplier}
      value={selectedValue}
      options={options}
      optionRender={optionRender}
      filterOption={false}
      onSearch={onSearch}
      onChange={handleChange}
      onOpenChange={(open) => {
        setSearch("")
        if (open) void load("")
      }}
      aria-label={t("finance.columns.material")}
      placeholder={
        supplier
          ? t("finance.material.placeholder")
          : t("finance.material.supplierFirst")
      }
      notFoundContent={
        loading
          ? t("finance.material.searching")
          : t("finance.material.notFound")
      }
      // A chevron, not a magnifier: this is a dropdown you open to browse the
      // supplier's price list, which happens to also filter as you type. A
      // search icon made it read as a text field with no list behind it.
      suffixIcon={loading ? <LoadingOutlined /> : undefined}
    />
  )
}
